import React, { Fragment, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import Button from 'chameleon/ui-stack/ui/base/button';
import NavItemsContainer from 'chameleon/ui-stack/ui/sidebar-two-levels/nav-items-container';
import LabelNavItem from 'chameleon/ui-stack/ui/sidebar-two-levels/label-nav-item';
import useNavigation, {
  PAGE_ACTIONS,
} from 'chameleon/ui-stack/hooks/useNavigation';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import {
  Context,
  toggleMainNav,
} from 'chameleon/ui-stack/ui/basic/main-nav-two-levels';

import DeleteLabelModal from './Labels/DeleteLabelModal';
import UpdateVisibilityModal from './Labels/UpdateVisibilityModal';
import useLabelModal from 'legacy-src/components/TeacherOrganize/hooks/useLabelModal';
import getLabelTooltipText from '../utils/getLabelTooltipText';

import {
  keys,
  treeArrowHandler,
  spaceAndEnterHandler,
} from 'chameleon/legacy/src/utility/accessibility';
import { LABEL_BUTTON_CLASS, LEFT_POPOVER_MENU_NAME } from '../utils/constants';
import { useTeacherOrganizeContext } from 'legacy-src/components/TeacherOrganize/hooks/useTeacherOrganize';
import TextWithTooltip from 'chameleon/ui-stack/ui/assessments/text-with-tooltip';
import { STATUS } from 'chameleon/src/constants/assessmentStatus';

const SidebarLabelDisplay = ({
  router,
  isParentExpanded,
  showTeacherOrganize,
  isTeacher,
  isCoordinator,
  isStudent,
  isAdmin,
}) => {
  const [, dispatch] = useContext(Context);
  const {
    params,
    addParamsToCurrentLocation,
    isAssignmentsPage,
    assignmentsPagePath,
    goTo,
    queryParams,
    isAssignmentsPageWithCreateEditLabelModal,
    isAssignmentsPageWithLabelVisibilityModal,
    cleanActionParams,
  } = useNavigation({ router });
  const { subject: masterSubject } = params || {};
  const isAdminOrCoordinator = isAdmin || isCoordinator;

  const {
    subjectLabels,
    isStudentVisible,
    labels,
    parents,
    children,
    actions: { deleteLabel, updateLabelsVisibility, reorderLabels },
    state: { isReordering, isTeacherInfoLoading },
  } = useTeacherOrganizeContext({ masterSubject });

  const getTeacherOrganizeLabelLink = ({ labels }) => {
    return addParamsToCurrentLocation(
      { labels },
      isAssignmentsPage ? null : `/${masterSubject}${assignmentsPagePath}`,
      [
        'page',
        'unit',
        'quizId',
        'editMode',
        'status',
        'type',
        'searchTerm',
        'section',
      ],
    );
  };

  const { labels: organizeLabelFilter = '' } = queryParams || {};
  const labelFilters = organizeLabelFilter.split(',');

  const focusOnMainHeading = () => {
    const element = document.getElementById('mainHeading');
    if (element) {
      element.focus();
    }
  };

  const handleKeyDown = (event, label) => {
    const keyCodes = Object.values(keys);
    if (keyCodes.includes(event.keyCode)) {
      treeArrowHandler(event, label);
      spaceAndEnterHandler(event);

      if (event.keyCode === keys.space || event.keyCode === keys.enter) {
        focusOnMainHeading();
      }
    }
  };

  const {
    LabelModal,
    createEditModalProps,
    isCreateEditModalVisible,
    isShowVisibilityModal,
    setShowVisibilityModal,
    createNewLabel: onCreateLabel,
    editLabel: onEditLabel,
  } = useLabelModal({ masterSubject, router });

  const [labelToDelete, setLabelToDelete] = useState(null);

  const onDeleteLabel = (label) => {
    setLabelToDelete(label);
  };

  const labelNavItemClick = (labelId) => {
    goTo(getTeacherOrganizeLabelLink({ labels: labelId }));
    toggleMainNav(dispatch, false);
  };

  useEffect(() => {
    if (isAssignmentsPageWithLabelVisibilityModal) {
      setShowVisibilityModal(true);
    }
    if (isAssignmentsPageWithCreateEditLabelModal) {
      onCreateLabel();
    }
  }, [
    isAssignmentsPageWithLabelVisibilityModal,
    isAssignmentsPageWithCreateEditLabelModal,
  ]);

  const onCreateLabelWithRedirect = () => {
    if (!isAssignmentsPage) {
      goTo(
        addParamsToCurrentLocation(
          {
            action: PAGE_ACTIONS.CREATE_LABEL,
            status: STATUS.ALL_RESOURCES,
          },
          `/${masterSubject}${assignmentsPagePath}`,
          ['page', 'unit', 'quizId', 'editMode'],
        ),
      );
    }
    onCreateLabel();
  };

  const onShowVisibilityModalWithRedirect = () => {
    if (!isAssignmentsPage) {
      goTo(
        addParamsToCurrentLocation(
          {
            action: PAGE_ACTIONS.SHOW_HIDE_LABEL,
            status: STATUS.ALL_RESOURCES,
          },
          `/${masterSubject}${assignmentsPagePath}`,
          ['page', 'unit', 'quizId', 'editMode'],
        ),
      );
    }
    setShowVisibilityModal(true);
  };

  const getTeacherOrganizeExampleModalLink = (labelMenuType) => {
    goTo(
      addParamsToCurrentLocation({
        assignmentExampleModal: labelMenuType,
      }),
    );
  };

  if (!showTeacherOrganize || !subjectLabels) {
    return null;
  }

  return (
    <Fragment>
      {isCreateEditModalVisible && <LabelModal {...createEditModalProps} />}
      {Boolean(labelToDelete) && (
        <DeleteLabelModal
          label={labelToDelete}
          onDelete={deleteLabel}
          onCancel={() => setLabelToDelete(null)}
        />
      )}
      {isShowVisibilityModal && (
        <UpdateVisibilityModal
          isStudentVisible={isStudentVisible}
          onConfirm={() => {
            updateLabelsVisibility(!isStudentVisible, masterSubject);
            cleanActionParams();
          }}
          onCancel={() => {
            setShowVisibilityModal(false);
            cleanActionParams();
          }}
          data-test-id="update-visibility-modal"
        />
      )}
      <div
        data-test-id="labels-sidebar-header"
        aria-hidden={!isParentExpanded}
        className={cn(
          'flex items-center justify-between text-gray-900 outline-none',
          'text-default font-medium p-1 pr-4 pl-4.6 h-20',
          'pt-6 mt-10 mb-6 border-t border-gray-600 border-opacity-30',
          {
            block: isParentExpanded,
            hidden: !isParentExpanded,
          },
        )}
      >
        <div className="flex items-center">
          <span className="text-base">
            {isTeacher || isAdminOrCoordinator ? 'Labels' : 'Teacher Labels'}
          </span>
          {isTeacher && (
            <TextWithTooltip
              tabIndex={-1}
              tooltip={getLabelTooltipText(isStudentVisible, labels)}
            >
              <Icon
                tabIndex={0}
                className="ml-4"
                accessibility={{
                  ariaLabel: isStudentVisible
                    ? 'Visible To Student'
                    : 'Not Visible to Student',
                }}
                name={isStudentVisible ? 'show' : 'hide'}
              />
            </TextWithTooltip>
          )}
        </div>
        <ButtonWithIcon
          className={cn({ hidden: isStudent })}
          isIcon
          isNaked
          accessibility={{ title: 'Create Label' }}
          icon="add"
          data-test-id={LABEL_BUTTON_CLASS.create}
          disabled={isTeacherInfoLoading}
          onClick={() =>
            isTeacher
              ? onCreateLabelWithRedirect()
              : getTeacherOrganizeExampleModalLink(
                  LEFT_POPOVER_MENU_NAME.create,
                )
          }
        />
      </div>

      {isTeacher && (
        <div
          className={cn('justify-center mb-8', {
            flex: isParentExpanded,
            hidden: !isParentExpanded,
          })}
          aria-hidden={!isParentExpanded}
        >
          <Button
            onClick={() => onShowVisibilityModalWithRedirect()}
            isNaked
            size="custom"
            className={cn(
              'text-blue-600 border border-blue-600 rounded-sm text-11 font-medium w-fit py-3',
            )}
            disabled={isEmpty(labels) || isTeacherInfoLoading}
            data-test-id={LABEL_BUTTON_CLASS.toggleLabelVisibility}
          >
            {isStudentVisible
              ? 'Hide labels from students'
              : 'Show labels to students'}
          </Button>
        </div>
      )}
      <NavItemsContainer
        isExpanded={isParentExpanded}
        id="items-container-labels"
        className="whitespace-normal"
        accessibility={{
          role: 'list',
          'aria-label': 'Labels',
          'aria-hidden': !isParentExpanded,
        }}
        tag="div"
      >
        {parents?.map((parentId, parentIndex) => {
          const label = labels[parentId] || {};
          const childLabels = children[parentId] || [];
          return (
            <Fragment key={`label-${parentId}`}>
              <LabelNavItem
                id={`list_item_${parentId}`}
                showActions={isTeacher}
                onEditLabel={onEditLabel}
                onDeleteLabel={onDeleteLabel}
                onCreateLabel={onCreateLabel}
                isReordering={isReordering}
                isTeacherInfoLoading={isTeacherInfoLoading}
                onMoveUp={() =>
                  reorderLabels(
                    [parentId, parents[parentIndex - 1]],
                    masterSubject,
                  )
                }
                onMoveDown={() =>
                  reorderLabels(
                    [parentId, parents[parentIndex + 1]],
                    masterSubject,
                  )
                }
                item={{
                  ...label,
                  onClick: () => labelNavItemClick(parentId),
                  isActive: labelFilters.includes(String(parentId)),
                }}
                handleKeyDown={handleKeyDown}
                accessibility={{
                  'aria-level': '1',
                  'aria-setsize': parents.length,
                  'aria-posinset': parentIndex + 1,
                  'data-parent': parentId,
                }}
                tag="div"
              >
                {childLabels.length > 0 ? (
                  <NavItemsContainer
                    isExpanded={isParentExpanded}
                    id={`items-container-label-children-${parentId}`}
                    accessibility={{
                      role: 'list',
                      'aria-labelledby': `list_item_${parentId}`,
                    }}
                    tag="div"
                  >
                    {childLabels.map((childLabelId, childIndex) => {
                      const childLabel = labels[childLabelId] || {};
                      return (
                        <Fragment
                          key={`child-label-${parentId}-${childLabelId}`}
                        >
                          <LabelNavItem
                            showActions={isTeacher}
                            onEditLabel={onEditLabel}
                            onDeleteLabel={onDeleteLabel}
                            onCreateLabel={onCreateLabel}
                            isReordering={isReordering}
                            isTeacherInfoLoading={isTeacherInfoLoading}
                            onMoveUp={() =>
                              reorderLabels(
                                [childLabelId, childLabels[childIndex - 1]],
                                masterSubject,
                              )
                            }
                            onMoveDown={() =>
                              reorderLabels(
                                [childLabelId, childLabels[childIndex + 1]],
                                masterSubject,
                              )
                            }
                            key={`child-label-${parentId}-${childLabelId}`}
                            item={{
                              ...childLabel,
                              onClick: () => labelNavItemClick(childLabelId),
                              isActive: labelFilters.includes(
                                String(childLabelId),
                              ),
                            }}
                            handleKeyDown={handleKeyDown}
                            accessibility={{
                              'aria-level': '2',
                              'aria-setsize': childLabels.length,
                              'aria-posinset': childIndex + 1,
                              'data-child-of': parentId,
                            }}
                            tag="div"
                          />
                        </Fragment>
                      );
                    })}
                  </NavItemsContainer>
                ) : null}
              </LabelNavItem>
            </Fragment>
          );
        })}
      </NavItemsContainer>
    </Fragment>
  );
};

SidebarLabelDisplay.propTypes = {
  isParentExpanded: PropTypes.bool,
  showTeacherOrganize: PropTypes.bool,
  isTeacher: PropTypes.bool,
  isCoordinator: PropTypes.bool,
  isStudent: PropTypes.bool,
  isAdmin: PropTypes.bool,
};

SidebarLabelDisplay.defaultProps = {
  isParentExpanded: false,
  showTeacherOrganize: false,
  isTeacher: false,
  isCoordinator: false,
  isStudent: false,
  isAdmin: false,
};

export default compose(
  withRouter,
  connect(
    ({
      featureFlag: { teacher_organize: showTeacherOrganize },
      userData: { isTeacher, isCoordinator, isStudent, isAdmin },
    }) => ({
      showTeacherOrganize,
      isTeacher,
      isCoordinator,
      isStudent,
      isAdmin,
    }),
  ),
)(SidebarLabelDisplay);
